import type { DiscoveryContent } from "@amzn/ads-discovery-theme-service-typescript-client";
import { PhoenixMetrics } from "@amzn/ads-phoenix-type-script-client";
import type { TemplateAssociations } from "@amzn/d16-g-cdb-publisher-portal-typescript-client";
import { ContentType } from "@amzn/g4sservice-client";
import type {
    ContentSelectionResponse,
    SelectedContent,
} from "@amzn/g4sservice-client";
import { useAsync } from "@react-hookz/web";
import { useCallback, useEffect } from "react";
import { useStore } from "zustand";

import { getG4SClient } from "../../api/g4sApi/g4s";
import { getPublisherPortalApiClient } from "../../api/publisherPortalApi/publisherPortalApi";
import { DeckDecorationLanguages } from "../../constants/deckDecoration";
import { G4sPlacement } from "../../constants/g4sPlacement";
import { TELEMETRY_ID, PHOENIX_FEATURE } from "../../constants/telemetry";
import { exploreWithAiStore } from "../../store/exploreWithAi";
import { isLoading } from "../../utils/api.utils";
import { isAccountAgnosticId } from "../../utils/permission.utils";
import { convertToSelectedContentInsights } from "../../utils/selectedContent.utils";
import { logWithTakt } from "../../utils/telemetry.utils";
import { useSingleMarketingNarrativeAction } from "../marketingContent/useSingleMarketingContentLoader";
import { useAsyncErrorStatusNotification } from "../notifications/useAsyncErrorStatusNotification";

const contentTypeFilters = [
    ContentType.INSIGHT,
    ContentType.MARKETING_CONTENT,
    ContentType.TEMPLATE,
];

const getDefaultContentTypes = (isAccountAgnostic: boolean) => {
    return isAccountAgnostic
        ? contentTypeFilters.filter(
              (contentType) => contentType !== ContentType.INSIGHT,
          )
        : contentTypeFilters;
};

export interface g4sContentSelectionLoaderProps {
    advertiserId?: string;
    marketplaceId?: string;
    isInitialLoad: boolean;
}

export const CONTENT_SELECTION_RESPONSE_ID_FAILURE =
    "CONTENT_SELECTION_RESPONSE_ID_FAILURE";

export function useG4SContentSelectionLoader({
    advertiserId,
    marketplaceId,
    isInitialLoad,
}: g4sContentSelectionLoaderProps): void {
    const [
        storePrompt,
        isStoreExamplePrompt,
        setStoreContent,
        setStoreResponseId,
        setStoreAsyncStatus,
        storeBrandId,
        storeBrowseNodeId,
        storeContentTypes,
    ] = useStore(exploreWithAiStore, (state) => [
        state.prompt,
        state.isExamplePrompt,
        state.setSelectedContent,
        state.setResponseId,
        state.setAsyncStatus,
        state.brandId,
        state.browseNodeId,
        state.contentTypes,
    ]);

    const [suggestionsState, suggestionsAction] = useAsync(
        async (
            prompt: string,
            brandId?: string,
            browseNodeId?: string,
            contentTypes?: ContentType[],
        ) => {
            const context = { userPrompt: prompt };
            const isAccountAgnostic = isAccountAgnosticId();
            const defaultContentTypes =
                getDefaultContentTypes(isAccountAgnostic);

            const isContentTypesAvailable =
                contentTypes && contentTypes.length > 0;

            const contentType = isContentTypesAvailable
                ? contentTypes
                : defaultContentTypes;

            const filters = {
                advertiserSalesforceId: advertiserId!,
                marketplaceId,
                contentType,
                language: DeckDecorationLanguages.ENGLISH,
                ...(!isAccountAgnostic && {
                    brands: brandId ? [brandId] : undefined,
                    browseNodes: browseNodeId ? [browseNodeId] : undefined,
                }),
            };

            const response: ContentSelectionResponse = await (
                await getG4SClient()
            ).getContentSelection(
                context,
                filters,
                G4sPlacement.EXPLORE_WITH_AI_PAGE,
            );

            if (isContentTypesAvailable) {
                logWithTakt(TELEMETRY_ID.G4S_CONTENT_TYPES_FILTER_APPLIED, {
                    contentTypes,
                });
                PhoenixMetrics.emit(
                    TELEMETRY_ID.G4S_CONTENT_TYPES_FILTER_APPLIED,
                    PHOENIX_FEATURE.AI,
                    {
                        customData: {
                            contentTypes,
                        },
                    },
                );
            }

            return response;
        },
    );
    useAsyncErrorStatusNotification(suggestionsState);

    const [singleMarketingContentState, singleMarketingContentAction] =
        useSingleMarketingNarrativeAction();

    const fetchMarketingContent = useCallback(
        async (
            selectedContents: SelectedContent[] | undefined,
        ): Promise<DiscoveryContent[]> => {
            if (
                !selectedContents ||
                isLoading(singleMarketingContentState.status)
            ) {
                return [];
            }

            const marketingContents = selectedContents.filter(
                (selectedContent) =>
                    selectedContent.type === ContentType.MARKETING_CONTENT &&
                    selectedContent.content?.["documentId"],
            );

            const marketingContentPromises = marketingContents.map(
                async (selectedContent) => {
                    try {
                        return await singleMarketingContentAction.execute(
                            selectedContent.content!["documentId"],
                        );
                    } catch (e) {
                        console.error(
                            `Failed to fetch marketing content for documentId: ${selectedContent.content!["documentId"]}`,
                            e,
                        );
                        return null;
                    }
                },
            );

            const results = await Promise.all(marketingContentPromises);
            return results.filter(
                (result): result is DiscoveryContent => result !== null,
            );
        },
        [singleMarketingContentAction, singleMarketingContentState],
    );

    useEffect(() => {
        const fetchSuggestions = async () => {
            if (storePrompt) {
                setStoreContent([]);
                setStoreResponseId("");
                setStoreAsyncStatus("loading");
                let templateAssociations: TemplateAssociations[] = [];
                await (
                    await getPublisherPortalApiClient()
                )
                    .getTemplatesWithAssociations({})
                    .then((response) => {
                        if (response.data) {
                            templateAssociations = response.data;
                        }
                    })
                    .catch((e: any) => {
                        console.error(e.message);
                    });
                let responseId = CONTENT_SELECTION_RESPONSE_ID_FAILURE;
                try {
                    const contentSelectionResponse =
                        await suggestionsAction.execute(
                            storePrompt,
                            storeBrandId,
                            storeBrowseNodeId,
                            storeContentTypes,
                        );
                    responseId = contentSelectionResponse.id!;
                    const marketingNarratives = await fetchMarketingContent(
                        contentSelectionResponse?.selectedContents,
                    );
                    setStoreContent(
                        convertToSelectedContentInsights(
                            contentSelectionResponse.selectedContents || [],
                            templateAssociations,
                            marketingNarratives,
                        ),
                    );
                    setStoreAsyncStatus("success");
                    setStoreResponseId(responseId);
                } catch (error) {
                    setStoreAsyncStatus("error");
                } finally {
                    logWithTakt(TELEMETRY_ID.G4S_INSIGHT_PROMPT_SUBMITTED, {
                        prompt: storePrompt,
                        isExamplePrompt: isStoreExamplePrompt,
                        responseId,
                    });
                    PhoenixMetrics.emit(
                        TELEMETRY_ID.G4S_INSIGHT_PROMPT_SUBMITTED,
                        PHOENIX_FEATURE.AI,
                        {
                            customData: {
                                prompt: storePrompt,
                                isExamplePrompt: isStoreExamplePrompt,
                                responseId,
                            },
                        },
                    );
                }
            }
        };
        if (!isInitialLoad) {
            void fetchSuggestions();
        }
    }, [storePrompt, storeBrandId, storeBrowseNodeId, storeContentTypes]);
}
